import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { adminApi } from "../../../lib/admin";
import type { AdminNotificationListItemResponse } from "../../../types/adminTypes";
import { apiErrorToMessage } from "../../../utils/ApiUtils";
import { formatDateTime } from "../../../utils/DateUtils";
import Button from "../../ui/Button/Button";
import Modal from "../../ui/Modal/Modal";
import AdminPillBadge from "../ui/AdminPillBadge";

type AdminNotificationDetailModalProps = {
  notification: AdminNotificationListItemResponse | null;
  isOpen: boolean;
  canWrite: boolean;
  onClose: () => void;
};

const statusTone: Record<AdminNotificationListItemResponse["status"], "warning" | "success" | "danger"> = {
  PENDING: "warning",
  SENT: "success",
  FAILED: "danger",
};

const AdminNotificationDetailModal = ({ notification, isOpen, canWrite, onClose }: AdminNotificationDetailModalProps) => {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState("");
  const [feedback, setFeedback] = useState<string | null>(null);

  const notificationDetailQuery = useQuery({
    queryKey: ["admin-notification-detail", notification?.id],
    queryFn: () => adminApi.getNotificationDetail(notification!.id),
    enabled: isOpen && notification !== null,
  });

  const requeueMutation = useMutation({
    mutationFn: () => adminApi.requeueNotification(notification!.id, { reason: reason.trim() }),
    onSuccess: async () => {
      setFeedback("Notification requeued.");
      setReason("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin-notifications"] }),
        queryClient.invalidateQueries({ queryKey: ["admin-notification-detail", notification?.id] }),
        queryClient.invalidateQueries({ queryKey: ["admin-audit"] }),
      ]);
    },
    onError: (error) => {
      setFeedback(apiErrorToMessage(error, "Unable to requeue notification."));
    },
  });

  const detail = notificationDetailQuery.data;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={notification ? notification.subject : "Notification detail"} panelClassName="max-w-4xl">
      {notificationDetailQuery.isLoading ? <p className="text-sm text-myteengig-text-muted">Loading notification detail...</p> : null}
      {notificationDetailQuery.error ? <p className="text-sm text-red-600">{apiErrorToMessage(notificationDetailQuery.error, "Failed to load notification detail.")}</p> : null}

      {detail ? (
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Status</p>
              <div className="mt-3"><AdminPillBadge tone={statusTone[detail.status]}>{detail.status}</AdminPillBadge></div>
            </div>
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Type</p>
              <div className="mt-3"><AdminPillBadge>{detail.type}</AdminPillBadge></div>
            </div>
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Channel</p>
              <p className="mt-3 text-sm text-myteengig-text-primary">{detail.channel}</p>
            </div>
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Attempts</p>
              <p className="mt-3 text-sm text-myteengig-text-primary">{detail.attemptCount}</p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">Delivery</p>
              <div className="mt-3 space-y-2 text-sm text-myteengig-text-muted">
                <p>{detail.recipientEmail}</p>
                <p>{detail.subject}</p>
                <p>Available: {formatDateTime(detail.availableAt)}</p>
                <p>Created: {formatDateTime(detail.createdAt)}</p>
                <p>Updated: {formatDateTime(detail.updatedAt)}</p>
              </div>
            </div>
            <div className="rounded-2xl border border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">Last error</p>
              <pre className="mt-3 whitespace-pre-wrap break-words rounded-xl bg-myteengig-background p-3 text-xs text-myteengig-text-muted">
                {detail.lastError ?? "No error recorded."}
              </pre>
            </div>
          </div>

          <div className="rounded-2xl border border-myteengig-border p-4">
            <p className="text-sm font-semibold text-myteengig-text-primary">Sanitized payload</p>
            <p className="mt-2 text-sm text-myteengig-text-muted">
              {detail.payloadRedacted
                ? "Sensitive values such as tokens and secrets are redacted in the admin view."
                : "No sensitive payload fields were detected for this notification."}
            </p>
            <pre className="mt-3 whitespace-pre-wrap break-words rounded-xl bg-myteengig-background p-3 text-xs text-myteengig-text-muted">
              {detail.payloadJson}
            </pre>
          </div>

          {detail.status === "FAILED" ? (
            <div className="rounded-2xl border border-dashed border-myteengig-border p-4">
              <p className="text-sm font-semibold text-myteengig-text-primary">Support action</p>
              <p className="mt-2 text-sm text-myteengig-text-muted">
                Requeue this failed notification only when you understand the failure mode. This action is written to the admin audit log.
              </p>
              <label htmlFor="admin-notification-requeue-reason" className="mt-4 block text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">
                Reason
              </label>
              <textarea
                id="admin-notification-requeue-reason"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                rows={3}
                className="mt-2 w-full rounded-lg border border-myteengig-border bg-white px-4 py-3 text-sm text-myteengig-text-primary shadow-sm outline-none transition focus:border-myteengig-primary focus:ring-2 focus:ring-myteengig-primary/20"
                placeholder="SES permission issue fixed. Requeueing failed verification email."
              />
              {feedback ? <p className="mt-3 text-sm text-myteengig-text-muted">{feedback}</p> : null}
              <div className="mt-4 flex justify-end">
                <Button disabled={!canWrite || reason.trim().length === 0 || requeueMutation.isPending} onClick={() => requeueMutation.mutate()}>
                  {requeueMutation.isPending ? "Requeueing..." : "Requeue Notification"}
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
};

export default AdminNotificationDetailModal;
